import * as React from 'react';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import TextField from '@mui/material/TextField';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';
import { upperFirst } from 'lodash';
import { TFunction } from 'i18next';

import { useSession } from '@hooks';
import { useListConversationsQuery, ListConversationsQuery } from '@graphql';
import { truncateText, getConversationTitle } from '@utils';

interface ConversationDrawerProps {
  handleOpenDialog: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseDrawer: () => void;
}

export function ConversationDrawer(props: Readonly<ConversationDrawerProps>) {
  const { handleOpenDialog, handleCloseDrawer } = props;
  const [conversations, setConversations] = React.useState<ListConversationsQuery['conversations']>([]);
  const theme = useTheme();
  const { account } = useSession();
  const { t } = useTranslation('conversation');

  const avatarSize = 50;

  const { data } = useListConversationsQuery({
    skip: !account,
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!account) {
      return;
    }

    const searchTerm = event.target.value.toLowerCase();
    if (data?.conversations) {
      const filteredConversations = data.conversations.filter((conversation) => {
        const title = getConversationTitle(account.id, conversation).toLowerCase();
        return title.includes(searchTerm);
      });
      setConversations(filteredConversations);
    } else {
      setConversations([]);
    }
  };

  React.useEffect(() => {
    setConversations(data?.conversations ?? []);
  }, [data]);

  if (!account) {
    return null;
  }

  return (
    <Box
      sx={{
        width: Device.isMobile ? '100vw' : 400,
        color: theme.palette.text.secondary,
        '& .MuiListItemButton-root': {
          color: theme.palette.text.secondary,
          '& .MuiListItemIcon-root, & .MuiListItemText-root': {
            color: theme.palette.text.secondary,
          },
          '&:hover': {
            color: theme.palette.text.primary,
            '& .MuiListItemIcon-root, & .MuiListItemText-root': {
              color: theme.palette.text.primary,
            },
          },
        },
        '& .MuiDivider-root': {
          backgroundColor: theme.palette.grey[700],
        },
      }}
      role="presentation"
    >
      <List
        subheader={
          <ListSubheader component="div" id="messages-subheader">
            <Stack
              direction="row"
              justifyContent={Device.isMobile ? 'space-between' : 'flex-end'}
              alignItems="center"
              py={2}
            >
              {Device.isMobile && (
                <Button
                  startIcon={<theme.icons.back />}
                  onClick={handleCloseDrawer}
                  // color="inherit"
                  size="small"
                  sx={{ color: 'white' }}
                >
                  {t('back', { ns: 'cta' })}
                </Button>
              )}
              <Button
                startIcon={<theme.icons.write />}
                color="uncolored"
                variant="outlined"
                size="small"
                ml="auto"
              >
                {t('new message', { ns: 'cta' })}
              </Button>
            </Stack>
          </ListSubheader>
        }
      >
        <Stack spacing={1}>
          <form noValidate autoComplete="off">
            <TextField
              placeholder={t('search', { ns: 'cta' })}
              size="small"
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: <theme.icons.search fontSize="small" color="uncolored" />,
                },
              }}
              sx={{ width: '100%', px: 2 }}
            />
          </form>
          <Typography variant="subtitle2" p={2} color="text.primary" fontWeight="bold">
            {t('Messages')}
          </Typography>
          {conversations.length ? (
            conversations
              .filter((conversation) => conversation.lastMessage)
              .sort((a, b) => {
                const dateA = a.lastMessage ? new Date(a.lastMessage.createdAt).getTime() : 0;
                const dateB = b.lastMessage ? new Date(b.lastMessage.createdAt).getTime() : 0;
                return dateB - dateA;
              })
              .map((conversation) => (
                <Stack
                  key={conversation.id}
                  onClick={handleOpenDialog}
                  data-id="message"
                  conversation-id={conversation.id}
                >
                  <ListItem disablePadding>
                    <ListItemButton width="100%">
                      <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
                        <Badge
                          color="error"
                          badgeContent={conversation.unreadCount}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                        >
                          {conversation.members.length > 2 ? (
                            <theme.icons.people
                              sx={{
                                width: avatarSize,
                                height: avatarSize,
                              }}
                            />
                          ) : (
                            <AvatarWithSkeleton
                              src={conversation.members.find((m) => m.id !== account.id)?.avatar?.url}
                              size={avatarSize}
                            />
                          )}
                        </Badge>
                        <Stack width="100%">
                          <Typography
                            variant="body2"
                            fontWeight={conversation.unreadCount > 0 ? 'bold' : 'normal'}
                            color="text.primary"
                          >
                            {getConversationTitle(account.id, conversation)}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {formatLastMessage(
                              conversation,
                              conversation.lastMessage!.sender.id === account.id,
                              t
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                </Stack>
              ))
          ) : (
            <Stack>
              <ListItem disablePadding>
                <ListItemButton width="100%" disabled>
                  <Stack direction="row" spacing={1} alignItems="center" width="100%">
                    <Typography variant="body2" fontWeight="bold">
                      {t('No message')}
                    </Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
            </Stack>
          )}
        </Stack>
      </List>
    </Box>
  );
}

interface AvatarWithSkeletonProps {
  src?: string;
  alt?: string;
  size?: number;
}

export function AvatarWithSkeleton({ src, alt, size = 50 }: AvatarWithSkeletonProps) {
  const [loaded, setLoaded] = React.useState(false);

  if (!src) {
    return <Avatar sx={{ width: size, height: size }} />;
  }

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      {!loaded && (
        <Skeleton
          variant="circular"
          width={size}
          height={size}
          sx={{ position: 'absolute', top: 0, left: 0 }}
        />
      )}
      <Avatar
        src={src}
        alt={alt}
        sx={{
          width: size,
          height: size,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
        imgProps={{
          onLoad: () => setLoaded(true),
          onError: () => setLoaded(true),
        }}
      />
    </Box>
  );
}

function formatLastMessage(
  conversation: ListConversationsQuery['conversations'][0],
  isMe: boolean,
  t: TFunction
) {
  if (!conversation.lastMessage) {
    return '';
  }
  const { unreadCount } = conversation;
  const { content, createdAt } = conversation.lastMessage;

  if (isMe) {
    return (
      <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" flex={1}>
        <Typography variant="caption" flex={1} noWrap>
          {upperFirst(t('word.sent', { ns: 'common' }))}
        </Typography>
        <Typography variant="caption" fontSize="0.6rem" noWrap>
          {moment(createdAt).fromNow()}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" flex={1}>
      <Typography variant="caption" fontWeight={unreadCount > 0 ? 'bold' : 'normal'} noWrap>
        {truncateText(content, 25)}
      </Typography>
      <Typography variant="caption" fontSize="0.6rem" noWrap>
        {moment(createdAt).fromNow()}
      </Typography>
    </Stack>
  );
}
